import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function PersonaggiPage() {
  const [selectedCharacter, setSelectedCharacter] = useState<string | null>(null);

  const characters = [
    {
      id: 'achille',
      name: 'Achille',
      title: 'Il Pelide, l\'Eroe Invincibile',
      role: 'Eroe Greco',
      image: 'https://public.readdy.ai/ai/img_res/0d9dc0970b0103a35d19fc3655f70596.jpg',
      description: 'Figlio di Peleo e della dea marina Teti, Achille è il più grande guerriero greco. La madre lo rese quasi invulnerabile immergendolo nel fiume Stige, ma il tallone con cui lo teneva rimase vulnerabile.',
      traits: ['Invincibile in battaglia', 'Orgoglioso e irascibile', 'Fedele all\'amicizia', 'Consapevole del proprio destino'],
      role_in_story: 'Protagonista assoluto del poema. La sua ira contro Agamennone e il successivo ritiro dalla battaglia causano la morte di molti Achei. Torna a combattere solo per vendicare l\'amico Patroclo, uccidendo Ettore in un duello memorabile.',
      symbolism: 'Achille rappresenta la scelta tra una vita lunga ma oscura e una breve ma gloriosa. Incarna l\'ideale eroico greco: il guerriero perfetto che sceglie consapevolmente la gloria immortale a scapito della vita.',
      quotes: ['"Meglio una breve vita gloriosa che una lunga esistenza nell\'oblio."', '"La mia ira sarà terribile come il fuoco che divora tutto."']
    },
    {
      id: 'ettore',
      name: 'Ettore',
      title: 'Il Domatore di Cavalli, Difensore di Troia',
      role: 'Eroe Troiano',
      image: 'https://readdy.ai/api/search-image?query=hector%20trojan%20hero%20noble%20warrior%20bronze%20armor%20helmet%20with%20plume%20defender%20protector%20classical%20greek%20art%20style%20dignified%20strong&width=600&height=800&seq=char-hector-001&orientation=portrait',
      description: 'Figlio maggiore di Priamo e Ecuba, marito di Andromaca e padre del piccolo Astianatte. È il più valoroso difensore di Troia e il comandante dell\'esercito troiano.',
      traits: ['Coraggioso e responsabile', 'Devoto alla famiglia', 'Senso del dovere', 'Umano e compassionevole'],
      role_in_story: 'Principale antagonista di Achille. Guida i Troiani in battaglia e uccide Patroclo, scatenando la vendetta di Achille. Il suo duello finale con Achille e la sua morte sono tra i momenti più toccanti del poema.',
      symbolism: 'Ettore rappresenta l\'eroe "umano", mosso dall\'amore per la famiglia e dal dovere verso la patria piuttosto che dalla sete di gloria. È il guerriero che combatte non per se stesso ma per proteggere chi ama.',
      quotes: ['"Un solo presagio è il migliore: combattere per la patria."', '"Verrà il giorno in cui cadrà la sacra Ilio, e Priamo e il popolo di Priamo."']
    },
    {
      id: 'agamennone',
      name: 'Agamennone',
      title: 'Re di Micene, Signore degli Uomini',
      role: 'Comandante Greco',
      image: 'https://readdy.ai/api/search-image?query=agamemnon%20king%20of%20mycenae%20royal%20crown%20scepter%20commanding%20presence%20regal%20armor%20greek%20king%20powerful%20leader%20classical%20art&width=600&height=800&seq=char-agamemnon-001&orientation=portrait',
      description: 'Re di Micene e fratello di Menelao, Agamennone è il comandante supremo dell\'esercito greco. Potente ma arrogante, il suo conflitto con Achille per la schiava Briseide innesca gli eventi del poema.',
      traits: ['Autoritario e orgoglioso', 'Abile stratega', 'Avido di potere', 'Rispettato ma non amato'],
      role_in_story: 'Il suo insulto ad Achille, sottraendogli la schiava Briseide, causa il ritiro dell\'eroe dalla battaglia. Rappresenta l\'autorità costituita e il potere politico, in contrasto con l\'eccellenza individuale di Achille.',
      symbolism: 'Agamennone incarna il potere regale e i suoi limiti. Mostra come l\'arroganza e l\'abuso di autorità possano avere conseguenze disastrose, anche per un re potente.',
      quotes: ['"Io sono il re, e la mia parola è legge nell\'esercito acheo."', '"Ho sbagliato, lo ammetto. Ma un re non può piegarsi facilmente."']
    },
    {
      id: 'patroclo',
      name: 'Patroclo',
      title: 'Il Compagno Fedele',
      role: 'Eroe Greco',
      image: 'https://readdy.ai/api/search-image?query=patroclus%20greek%20warrior%20young%20hero%20gentle%20face%20wearing%20achilles%20armor%20loyal%20companion%20classical%20sculpture%20emotional%20expression&width=600&height=800&seq=char-patroclus-001&orientation=portrait',
      description: 'Cugino e carissimo amico di Achille, Patroclo è un guerriero valoroso ma più gentile e compassionevole del Pelide. La loro amicizia è uno dei legami più profondi dell\'epica greca.',
      traits: ['Leale e devoto', 'Compassionevole', 'Coraggioso', 'Mediatore'],
      role_in_story: 'Vedendo i Greci in difficoltà, Patroclo convince Achille a prestargli l\'armatura e guida i Mirmidoni in battaglia. Uccide molti Troiani ma viene infine abbattuto da Ettore con l\'aiuto di Apollo. La sua morte spinge Achille a tornare in guerra.',
      symbolism: 'Patroclo rappresenta l\'umanità e la compassione in mezzo alla violenza della guerra. La sua morte è il catalizzatore emotivo del poema, trasformando l\'ira di Achille in dolore e sete di vendetta.',
      quotes: ['"Non posso restare a guardare mentre i nostri compagni muoiono."', '"Achille, ricorda la tua umanità."']
    },
    {
      id: 'priamo',
      name: 'Priamo',
      title: 'Re di Troia',
      role: 'Re Troiano',
      image: 'https://readdy.ai/api/search-image?query=priam%20king%20of%20troy%20elderly%20wise%20ruler%20white%20beard%20royal%20robes%20dignified%20sad%20expression%20father%20figure%20classical%20art&width=600&height=800&seq=char-priam-001&orientation=portrait',
      description: 'Anziano re di Troia, padre di cinquanta figli tra cui Ettore e Paride. Assiste impotente alla distruzione della sua famiglia e della sua città.',
      traits: ['Saggio e dignitoso', 'Padre amorevole', 'Coraggioso nell\'umiliazione', 'Simbolo della sofferenza'],
      role_in_story: 'Nel momento più toccante del poema, Priamo si reca da solo nella tenda di Achille per supplicarlo di restituire il corpo di Ettore. Questo incontro tra il vecchio re e l\'uccisore di suo figlio è un momento di profonda umanità.',
      symbolism: 'Priamo rappresenta la dignità nella sconfitta e il dolore universale di un padre che perde i figli. Il suo coraggio nel supplicare Achille mostra che la vera grandezza non sta solo nella forza ma anche nell\'umiltà.',
      quotes: ['"Bacia la mano che ha ucciso i miei figli."', '"Ricordati di tuo padre, Achille, e abbi pietà di me."']
    },
    {
      id: 'elena',
      name: 'Elena',
      title: 'La Donna più Bella del Mondo',
      role: 'Causa della Guerra',
      image: 'https://readdy.ai/api/search-image?query=helen%20of%20troy%20beautiful%20woman%20elegant%20greek%20dress%20classical%20beauty%20sad%20expression%20regretful%20ancient%20greece%20feminine%20grace&width=600&height=800&seq=char-helen-001&orientation=portrait',
      description: 'Moglie di Menelao, rapita (o fuggita) con Paride a Troia. La sua bellezza leggendaria è la causa scatenante della guerra, ma nell\'Iliade appare tormentata dal senso di colpa.',
      traits: ['Bellissima', 'Consapevole della propria colpa', 'Intelligente', 'Malinconica'],
      role_in_story: 'Sebbene non sia un personaggio d\'azione, Elena è presente in scene chiave. Osserva la battaglia dalle mura di Troia, piange Ettore e riflette sul destino che la sua bellezza ha causato.',
      symbolism: 'Elena rappresenta il potere distruttivo della bellezza e del desiderio. È insieme vittima e causa della guerra, simbolo dell\'ambiguità morale e della complessità umana.',
      quotes: ['"Avrei dovuto morire prima di causare tanto dolore."', '"Sono odiata da tutti, troiani e greci."']
    },
    {
      id: 'zeus',
      name: 'Zeus',
      title: 'Padre degli Dei e degli Uomini',
      role: 'Re degli Dei',
      image: 'https://readdy.ai/api/search-image?query=zeus%20greek%20god%20king%20of%20olympus%20powerful%20majestic%20white%20beard%20lightning%20bolt%20throne%20divine%20authority%20classical%20sculpture&width=600&height=800&seq=char-zeus-001&orientation=portrait',
      description: 'Re dell\'Olimpo e dio del cielo e del fulmine. Cerca di mantenere un equilibrio tra le fazioni divine, ma è anche influenzato dalle suppliche e dalle promesse.',
      traits: ['Potente e autorevole', 'Giusto ma non imparziale', 'Soggetto al Fato', 'Padre e marito'],
      role_in_story: 'Zeus promette a Teti di onorare Achille facendo vincere i Troiani finché i Greci non riconosceranno il valore del Pelide. Interviene in momenti cruciali ma non può (o non vuole) cambiare il destino finale.',
      symbolism: 'Zeus rappresenta l\'ordine cosmico e il potere supremo, ma anche i suoi limiti. Nemmeno il re degli dei può sfuggire al Fato, mostrando che esiste una legge superiore anche alla volontà divina.',
      quotes: ['"Il destino è scritto, e nemmeno io posso cambiarlo."', '"Guai a chi sfida la volontà di Zeus!"']
    },
    {
      id: 'atena',
      name: 'Atena',
      title: 'Dea della Saggezza e della Guerra',
      role: 'Dea Protettrice dei Greci',
      image: 'https://readdy.ai/api/search-image?query=athena%20greek%20goddess%20wisdom%20war%20helmet%20aegis%20shield%20owl%20wise%20powerful%20feminine%20strength%20classical%20sculpture&width=600&height=800&seq=char-athena-001&orientation=portrait',
      description: 'Figlia di Zeus, dea della saggezza, della strategia militare e delle arti. Protettrice dei Greci, in particolare di Odisseo e Diomede.',
      traits: ['Intelligente e strategica', 'Guerriera abile', 'Protettrice degli eroi', 'Nemica di Troia'],
      role_in_story: 'Interviene ripetutamente per aiutare i Greci, ispirando i guerrieri e ingannando i Troiani. Nel duello finale, inganna Ettore facendogli credere di avere un alleato, garantendo così la vittoria di Achille.',
      symbolism: 'Atena rappresenta l\'intelligenza applicata alla guerra, la strategia che prevale sulla forza bruta. È la dea della civiltà contro la barbarie.',
      quotes: ['"La vittoria appartiene a chi usa la mente oltre che il braccio."', '"Proteggerò i Greci fino alla caduta di Troia."']
    },
    {
      id: 'apollo',
      name: 'Apollo',
      title: 'Dio del Sole, della Musica e della Profezia',
      role: 'Dio Protettore dei Troiani',
      image: 'https://readdy.ai/api/search-image?query=apollo%20greek%20god%20sun%20music%20golden%20hair%20lyre%20bow%20arrows%20beautiful%20youthful%20divine%20radiance%20classical%20art&width=600&height=800&seq=char-apollo-001&orientation=portrait',
      description: 'Figlio di Zeus e Leto, dio dalle molteplici funzioni. Protettore di Troia e nemico dei Greci, specialmente dopo l\'insulto al suo sacerdote Crise.',
      traits: ['Bello e terribile', 'Vendicativo', 'Protettore di Troia', 'Arciere divino'],
      role_in_story: 'Scatena la peste nel campo greco all\'inizio del poema. Protegge i Troiani in battaglia, aiuta Ettore a uccidere Patroclo e guiderà la freccia di Paride che ucciderà Achille (evento narrato in altri poemi).',
      symbolism: 'Apollo rappresenta la bellezza e la cultura, ma anche la vendetta divina. Mostra che gli dei possono essere tanto crudeli quanto benevoli.',
      quotes: ['"La mia ira cadrà sui Greci come frecce dal cielo."', '"Troia è sotto la mia protezione."']
    }
  ];

  const handleCharacterClick = (id: string) => {
    setSelectedCharacter(selectedCharacter === id ? null : id);
    if (selectedCharacter !== id) {
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-red-100 px-4 py-2 rounded-full mb-6">
            <i className="ri-team-line text-red-600"></i>
            <span className="text-red-800 text-sm font-semibold whitespace-nowrap">Eroi e Divinità</span>
          </div>
          <h1 className="font-serif text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Personaggi Principali<br />dell'Iliade
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl">
            Scopri gli eroi, i re e gli dei che animano il poema: le loro virtù, i loro difetti e il loro ruolo nella narrazione epica.
          </p>
        </div>
      </section>

      {/* Characters Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {characters.map((character) => (
              <div key={character.id} id={character.id}>
                <div 
                  onClick={() => handleCharacterClick(character.id)}
                  className="group cursor-pointer"
                >
                  <div className="overflow-hidden rounded-2xl mb-4 h-96 relative">
                    <img 
                      src={character.image}
                      alt={character.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold mb-2 whitespace-nowrap">
                        {character.role}
                      </span>
                      <h3 className="text-2xl font-bold text-white">{character.name}</h3>
                      <p className="text-white/90 text-sm">{character.title}</p>
                    </div>
                  </div>
                </div>

                {selectedCharacter === character.id && (
                  <div className="bg-gray-50 rounded-2xl p-8 mt-4 space-y-6 animate-fadeIn">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Chi è</h4>
                      <p className="text-gray-700 leading-relaxed">{character.description}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-3">Caratteristiche</h4>
                      <div className="flex flex-wrap gap-2">
                        {character.traits.map((trait, index) => (
                          <span key={index} className="bg-white px-3 py-1 rounded-full text-sm text-gray-700 border border-gray-200 whitespace-nowrap">
                            {trait}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Ruolo nella Narrazione</h4>
                      <p className="text-gray-700 leading-relaxed">{character.role_in_story}</p>
                    </div>

                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Significato Simbolico</h4>
                      <p className="text-gray-700 leading-relaxed">{character.symbolism}</p>
                    </div>

                    {character.quotes && (
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-3">Citazioni Memorabili</h4>
                        <div className="space-y-3">
                          {character.quotes.map((quote, index) => (
                            <blockquote key={index} className="border-l-4 border-orange-500 pl-4 italic text-gray-700">
                              {quote}
                            </blockquote>
                          ))}
                        </div>
                      </div>
                    )}

                    <button 
                      onClick={() => setSelectedCharacter(null)}
                      className="text-orange-600 hover:text-orange-700 font-semibold flex items-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-up-line"></i>
                      Chiudi
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Relationships */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Relazioni tra i Personaggi</h2>
          <div className="bg-white rounded-3xl p-12 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-sword-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Achille vs Agamennone</h3>
                    <p className="text-gray-700 text-sm">Conflitto per l'onore e il potere. Il loro scontro innesca tutti gli eventi del poema.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Achille e Patroclo</h3>
                    <p className="text-gray-700 text-sm">Amicizia profondissima. La morte di Patroclo trasforma Achille.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-cross-line text-indigo-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Achille vs Ettore</h3>
                    <p className="text-gray-700 text-sm">Nemici destinati. Il loro duello è il culmine del poema.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-parent-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Ettore e Andromaca</h3>
                    <p className="text-gray-700 text-sm">Amore coniugale. Il loro addio è una delle scene più toccanti.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-hand-heart-line text-violet-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Priamo e Achille</h3>
                    <p className="text-gray-700 text-sm">Da nemici a esseri umani che condividono il dolore. Momento di massima umanità.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-sparkling-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Zeus e Teti</h3>
                    <p className="text-gray-700 text-sm">La dea supplica Zeus di onorare suo figlio Achille, ottenendo una promessa fatale.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-star-line text-rose-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Atena e i Greci</h3>
                    <p className="text-gray-700 text-sm">Protezione divina. La dea interviene costantemente per favorire i Greci.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-sun-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Apollo e Troia</h3>
                    <p className="text-gray-700 text-sm">Protezione divina. Apollo difende i Troiani e punisce i Greci.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <Link to="/contesto-storico" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="whitespace-nowrap">Contesto Storico</span>
            </Link>
            <Link to="/timeline" className="flex items-center gap-2 text-orange-600 hover:text-orange-700 transition-colors font-semibold cursor-pointer">
              <span className="whitespace-nowrap">Timeline degli Eventi</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
